/**
 * Rotatable, animated 3D preview of the player's own survivor — the character
 * half of the paperdoll.
 *
 * **Uses the PLAYER animator, not the NPC one**, which is what makes this an
 * equipment preview rather than a mannequin: `createPlayerAnimator` derives its
 * arms pose family from `gameStateStore`'s equipment on every update and calls
 * `updateWeaponVisual`, so equipping an axe puts the axe in the survivor's hand
 * with the right hold, live, with nothing to wire here. Fed a permanently idle
 * input — a paperdoll never locomotes.
 *
 * Three things this has to get right, all of them things that bit this codebase
 * before:
 *  1. **Never render while the panel is shut.** The paperdoll starts hidden and
 *     is closed most of the session, so the renderer, the GLB and the animator
 *     are all created on the FIRST `setActive(true)` and the loop stops on
 *     `setActive(false)`. A WebGL context is scarce (the HUD portrait already
 *     holds one) and a hidden canvas rendering at 30Hz is pure waste.
 *  2. **Detach the model, never dispose it.** `load-model.ts` caches one root
 *     per url+height and hands out clones that SHARE geometry and materials
 *     with the settlement's own player model. Freeing them here would corrupt
 *     the live scene — the same trap `render/scene.ts` documents around
 *     `userData.sharedAsset`.
 *  3. **Reduced motion needs the POSE, not a skipped animator.** The rest pose
 *     of every character GLB is a T-pose, so "honour the preference by not
 *     animating" renders a T-posed survivor — the exact defect this preview
 *     exists to fix — for precisely the users who opted out of motion. It runs
 *     the animator until the pose settles, then stops on a static frame, and
 *     re-settles when equipment changes so a newly equipped weapon still shows.
 *
 * Dragging rotates the survivor and permanently cancels the auto-turn: once
 * someone has posed the model themselves, spinning it back under them is
 * hostile. Dragging is user-initiated, so it stays live under reduced motion.
 *
 * Fails safe the way the HUD portrait does: no WebGL, or a GLB that will not
 * load, leaves an empty box rather than breaking the panel.
 *
 * ─── Try-on (Atelier Hollis, 2026-10-17) ────────────────────────────────────
 * `setFit` lays pieces over what the player wears, for the PREVIEW ONLY. The
 * animator reads its equipment through a source (`AnimatorEquipmentSource`,
 * made injectable for the arena opponent); this preview's source is the
 * player's sheet with the fitted slots on top. Nothing here writes
 * `gameStateStore`: a fitted piece is never equipped, and `atelier-fit.test.ts`
 * holds that, mutation-checked.
 *
 * `mount` can be called again with another host: the Atelier borrows the
 * paperdoll's preview (`wardrobe-preview.ts`) rather than opening a WebGL
 * context of its own, and the two panels are never open together.
 */
import * as THREE from 'three';
import { CHARACTERS } from '../../character-select/characters';
import { loadCharacterModel } from '../../character-select/load-model';
import { gameStateStore } from '../../state/game-state';
import {
  createPlayerAnimator, LOCAL_EQUIPMENT_SOURCE,
  type AnimatorEquipmentSource, type PlayerAnimator, type PlayerAnimInput,
} from '../render/character-animations';
import type { EquippedItemSummary } from '../../state/game-state.types';

/** Pieces to try on, by equipment slot. Never written to any store. */
export type FitOverride = Partial<Record<string, EquippedItemSummary>>;

const MODEL_HEIGHT = 1.8;
/** Camera distance that keeps a turned survivor and the longest weapon in frame. */
const CAMERA_DISTANCE = 6.2;

/** ~30Hz. Higher than the HUD portrait's 18Hz: this one is large, on top, and being looked at deliberately. */
const RENDER_INTERVAL_MS = 33;

/** A paperdoll never moves. `dialogOpen` false — the survivor is not talking to themselves. */
const IDLE_INPUT: PlayerAnimInput = {
  moving: false, gait: 'walk', crouched: false, aiming: false, dialogOpen: false,
};

/** Long enough to cover the clips' async load plus the 0.25s crossfade into the idle. */
const REDUCED_MOTION_SETTLE_SECONDS = 2;

const AUTO_TURN_RATE = 0.5;
const AUTO_TURN_SWEEP = 0.5;
/**
 * Turned toward profile, not frontal — chosen against the ARMED silhouette.
 * A weapon is held pointing along the survivor's forward axis, so at a
 * near-frontal yaw the rifle-family weapons point away from the camera and
 * read end-on: with `weapon_bullpup-classic` and `weapon_shotgun-classic`
 * equipped I first took them for missing entirely, then found them by turning
 * the model. Rotating away from frontal makes the held weapon the readable
 * thing, which is the point of an equipment panel. The sweep still brings the
 * face round at the near end of its arc.
 *
 * 0.5 rather than further round, because the two weapon families want opposite
 * things and this is the compromise both tolerate: firearms point along the
 * forward axis and need the body turned to be seen at all, while the axe and
 * hockey-stick holds carry across the chest and start overlapping the head
 * past ~0.8. Both were checked at each value rather than reasoned about.
 */
const BASE_YAW = 0.5;
const DRAG_RADIANS_PER_PX = 0.012;

export class CharacterPreview {
  private host: HTMLElement | null = null;
  private renderer: THREE.WebGLRenderer | null = null;
  private scene: THREE.Scene | null = null;
  private camera: THREE.PerspectiveCamera | null = null;
  private model: THREE.Object3D | null = null;
  private animator: PlayerAnimator | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private ro: ResizeObserver | null = null;
  private unsubStore: (() => void) | null = null;

  private raf = 0;
  private clock = 0;
  private lastTime = 0;
  private lastRender = 0;
  private active = false;
  private started = false;
  private disposed = false;

  private yaw = BASE_YAW;
  private userPosed = false;
  private dragPointer: number | null = null;
  private dragLastX = 0;

  private reducedMotionQuery: MediaQueryList | null = null;
  private readonly onReducedMotionChange = (): void => this.restartLoop();

  private fit: FitOverride | null = null;
  private readonly fitListeners = new Set<() => void>();
  /** The worn sheet, with any fitted pieces on top. See the class docstring. */
  private readonly equipment: AnimatorEquipmentSource = {
    slots: () => {
      const worn = LOCAL_EQUIPMENT_SOURCE.slots();
      return this.fit ? { ...(worn ?? {}), ...this.fit } : worn;
    },
    subscribe: (onChange) => {
      const off = LOCAL_EQUIPMENT_SOURCE.subscribe(onChange);
      this.fitListeners.add(onChange);
      return () => { off(); this.fitListeners.delete(onChange); };
    },
  };

  /**
   * Attach to a preview box. Builds nothing until the panel first opens. Called
   * again with a different host, it moves the live canvas there.
   */
  mount(host: HTMLElement): void {
    if (this.host === host) return;
    this.host = host;
    if (!this.canvas) return;
    if (host.isConnected) {
      const position = getComputedStyle(host).position;
      if (!position || position === 'static') host.style.position = 'relative';
    }
    host.appendChild(this.canvas);
    this.ro?.disconnect();
    this.ro?.observe(host);
    this.resize();
  }

  /** Try pieces on (null takes them all off). Preview only: no store is written. */
  setFit(fit: FitOverride | null): void {
    this.fit = fit && Object.keys(fit).length > 0 ? { ...fit } : null;
    for (const notify of this.fitListeners) notify();
    // Under reduced motion the loop has stopped on a settled frame; re-settle
    // so the new piece is drawn, exactly as an equip does.
    if (this.active && this.reducedMotionQuery?.matches) this.restartLoop();
  }

  /** What is fitted now, for tests and the stage's pins. */
  get fitted(): FitOverride | null {
    return this.fit;
  }

  /**
   * Called by the panel on show/hide. Idempotent, so the paperdoll can call it
   * from both `show()` and `render()` without tracking whether it already has.
   */
  setActive(active: boolean): void {
    if (this.disposed || this.active === active) return;
    this.active = active;
    if (!active) {
      this.stopLoop();
      return;
    }
    if (!this.started) {
      this.started = true;
      this.build();
    }
    this.restartLoop();
  }

  private build(): void {
    const host = this.host;
    if (!host) return;

    /**
     * The canvas is `position: absolute; inset: 0` (`.bv-cp-canvas`), so it
     * sizes against its nearest POSITIONED ancestor — not necessarily this
     * host. A host that forgot `position: relative` does not clip the preview
     * or shrink it; the canvas escapes and stretches to whatever container
     * does happen to be positioned. In the arena's confirm modal that was the
     * full-screen scrim, so the survivor rendered across the middle of the
     * screen at viewport size.
     *
     * That is an invariant of THIS component (it is the thing that decided to
     * position its canvas absolutely), so it is enforced here rather than left
     * as a rule every future host has to remember. Only applied when the host
     * is still `static`, so a host with its own `absolute`/`fixed`/`sticky`
     * positioning is left alone.
     *
     * Before the WebGL try/catch on purpose: it is a layout concern, it costs
     * nothing when WebGL is unavailable, and it keeps the guard testable in
     * jsdom (which has no WebGL and would otherwise bail before reaching it).
     */
    if (host.isConnected) {
      // Treat an EMPTY computed position as unpositioned too, not just the
      // literal 'static'. Browsers resolve it to 'static'; jsdom returns ''
      // for an element no stylesheet has touched. Matching only 'static' would
      // work in production and silently no-op under test, which is the worst
      // of both — the guard would look covered and not be.
      const position = getComputedStyle(host).position;
      if (!position || position === 'static') host.style.position = 'relative';
    }

    let renderer: THREE.WebGLRenderer;
    try {
      const canvas = document.createElement('canvas');
      canvas.className = 'bv-cp-canvas';
      renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    } catch {
      return; // no WebGL — the styled empty box is an acceptable fallback
    }
    renderer.setClearColor(0x000000, 0);
    this.renderer = renderer;
    this.canvas = renderer.domElement;
    host.appendChild(this.canvas);

    const scene = new THREE.Scene();
    this.scene = scene;

    // Framing is set by the ARMED silhouette, not the body. A 1.8m model fits
    // head-to-toe at z=4, but the two-handed rifle hold puts the hands well
    // out to one side and the weapon (the bolt-action is 1.20m) further still,
    // so at that distance the arm and the whole gun fell outside the viewport
    // — measured in the browser, not guessed. Backed off far enough that a
    // turned survivor holding the longest weapon stays inside the box.
    const camera = new THREE.PerspectiveCamera(30, 1, 0.1, 20);
    camera.position.set(0, 1.0, CAMERA_DISTANCE);
    camera.lookAt(0, 0.9, 0);
    this.camera = camera;

    // Same warm-key/cool-rim mood as the settlement, the HUD portrait and the
    // dialog viewer, but brighter: this is the one place the player inspects
    // their own gear, so readability beats atmosphere.
    scene.add(new THREE.HemisphereLight(0x9fb8c9, 0x140d08, 0.85));
    const key = new THREE.DirectionalLight(0xffd2a8, 1.5);
    key.position.set(1.8, 2.6, 2.6);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0x7fa2b5, 0.7);
    rim.position.set(-2.2, 1.6, -1.6);
    scene.add(rim);

    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(host);
    this.resize();

    this.canvas.addEventListener('pointerdown', this.onPointerDown);
    this.canvas.addEventListener('pointermove', this.onPointerMove);
    this.canvas.addEventListener('pointerup', this.onPointerUp);
    this.canvas.addEventListener('pointercancel', this.onPointerUp);

    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.reducedMotionQuery.addEventListener('change', this.onReducedMotionChange);

    // Under reduced motion the loop stops once the pose has settled, so an
    // equip after that would never reach the animator and the weapon would
    // not appear. Re-settle on any state change instead of special-casing
    // equipment: the store only pushes when something actually changed.
    this.unsubStore = gameStateStore.subscribe(() => {
      if (this.active && this.reducedMotionQuery?.matches) this.restartLoop();
    });

    void this.loadModel();
  }

  private async loadModel(): Promise<void> {
    const { character } = gameStateStore.get();
    const presetId = character?.state === 'ACTIVE' ? character.presetId : undefined;
    // Falls back to the first preset for an unrecognized id, matching
    // `bootstrap.ts` — a known-rigged body beats an empty box.
    const def = CHARACTERS.find((c) => c.id === presetId) ?? CHARACTERS[0];
    if (!def) return;
    try {
      const model = await loadCharacterModel(def.modelUrl, MODEL_HEIGHT);
      if (this.disposed || !this.scene) return; // disposed while the GLB was in flight
      this.model = model;
      this.scene.add(model);
      // No-ops on an unrigged preset rather than throwing, which would leave
      // the panel broken instead of merely static.
      this.animator = createPlayerAnimator(model, this.equipment);
      this.restartLoop();
    } catch (err) {
      console.warn('[character-preview] model failed to load', err);
    }
  }

  private resize(): void {
    if (!this.renderer || !this.camera || !this.canvas) return;
    const host = this.canvas.parentElement;
    const w = Math.max(1, host?.clientWidth ?? 1);
    const h = Math.max(1, host?.clientHeight ?? 1);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(w, h, false);
    // The preview is shared between hosts, so framing travels with the host:
    // `data-cp-distance` pulls the camera in (the Survivor Record's box), and
    // a host without it keeps the default that fits the longest rifle turned.
    const distance = Number(host?.dataset.cpDistance) || CAMERA_DISTANCE;
    this.camera.position.set(0, 1.0, distance);
    this.camera.lookAt(0, 0.9, 0);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderFrame();
  }

  private restartLoop(): void {
    this.stopLoop();
    if (!this.active || this.disposed) return;
    this.clock = 0;
    this.lastTime = 0;
    this.lastRender = 0; // so the first frame after a (re)start draws immediately
    const loop = (now: number): void => {
      this.raf = requestAnimationFrame(loop);
      this.tick(now);
    };
    this.raf = requestAnimationFrame(loop);
  }

  private stopLoop(): void {
    cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  private tick(now: number): void {
    if (now - this.lastRender < RENDER_INTERVAL_MS) return;
    // dt spans the whole throttled interval, not one display frame, so the
    // turn runs at the same speed regardless of the cap or the refresh rate.
    const dt = this.lastTime ? Math.min((now - this.lastTime) / 1000, 0.2) : 0;
    this.lastTime = now;
    this.lastRender = now;
    this.clock += dt;

    this.animator?.update(dt, IDLE_INPUT);

    const reduced = this.reducedMotionQuery?.matches ?? false;
    if (!reduced && !this.userPosed) this.yaw = BASE_YAW + Math.sin(this.clock * AUTO_TURN_RATE) * AUTO_TURN_SWEEP;
    if (this.model) this.model.rotation.y = this.yaw;
    this.renderFrame();

    if (reduced && this.clock >= REDUCED_MOTION_SETTLE_SECONDS) this.stopLoop();
  }

  private renderFrame(): void {
    if (!this.renderer || !this.scene || !this.camera) return;
    this.renderer.render(this.scene, this.camera);
  }

  private readonly onPointerDown = (e: PointerEvent): void => {
    if (this.dragPointer !== null) return;
    this.dragPointer = e.pointerId;
    this.dragLastX = e.clientX;
    this.userPosed = true; // auto-turn never resumes — see the class docstring
    this.canvas?.setPointerCapture(e.pointerId);
    e.preventDefault();
  };

  private readonly onPointerMove = (e: PointerEvent): void => {
    if (this.dragPointer !== e.pointerId) return;
    this.yaw += (e.clientX - this.dragLastX) * DRAG_RADIANS_PER_PX;
    this.dragLastX = e.clientX;
    if (this.model) this.model.rotation.y = this.yaw;
    // Rendered here rather than left to the loop: under reduced motion the
    // loop has stopped, and a drag that does not redraw looks broken.
    this.renderFrame();
  };

  private readonly onPointerUp = (e: PointerEvent): void => {
    if (this.dragPointer !== e.pointerId) return;
    this.dragPointer = null;
    if (this.canvas?.hasPointerCapture(e.pointerId)) this.canvas.releasePointerCapture(e.pointerId);
  };

  /** Fully release GPU + DOM + listeners. Safe to call twice, and before `build()` ever ran. */
  dispose(): void {
    this.disposed = true;
    this.active = false;
    this.stopLoop();

    this.unsubStore?.();
    this.unsubStore = null;
    this.fit = null;
    this.fitListeners.clear();

    if (this.reducedMotionQuery) {
      this.reducedMotionQuery.removeEventListener('change', this.onReducedMotionChange);
      this.reducedMotionQuery = null;
    }
    this.ro?.disconnect();
    this.ro = null;

    if (this.canvas) {
      this.canvas.removeEventListener('pointerdown', this.onPointerDown);
      this.canvas.removeEventListener('pointermove', this.onPointerMove);
      this.canvas.removeEventListener('pointerup', this.onPointerUp);
      this.canvas.removeEventListener('pointercancel', this.onPointerUp);
    }

    // Before detaching: the animator's mixer is bound to this model's bones,
    // and it also owns the weapon it parented to `hand.R`.
    this.animator?.dispose();
    this.animator = null;

    // Detach only — the clone's geometry/materials are the very objects
    // load-model.ts's cache (and the settlement's player model) hold.
    this.model?.removeFromParent();
    this.model = null;

    this.scene?.clear();
    this.scene = null;
    this.camera = null;

    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss();
      this.renderer = null;
    }
    this.canvas?.remove();
    this.canvas = null;
    this.host = null;
  }
}
